import { useMemo, useState, type FormEvent } from "react";
import {
  Download,
  Eye,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type {
  CreatureDefinition,
  CreatureFeature,
} from "../types";
import { SrdCreatureImporter } from "./SrdCreatureImporter";

interface CreatureMasterProps {
  creatures: CreatureDefinition[];
  onAdd: (creature: CreatureDefinition) => void;
  onUpdate: (creature: CreatureDefinition) => void;
  onDelete: (id: string) => void;
  newId: () => string;
  now: () => string;
}

const abilityKeys = ["str", "dex", "con", "int", "wis", "cha"] as const;

function numberOrUndefined(value: FormDataEntryValue | null) {
  const raw = String(value || "").trim();
  if (!raw) return undefined;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function csv(value: FormDataEntryValue | null) {
  return String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function recordFromLines(value: FormDataEntryValue | null) {
  const result: Record<string, string | number> = {};

  for (const line of String(value || "").split("\n")) {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey?.trim();
    const rawValue = rest.join(":").trim();

    if (!key || !rawValue) continue;

    const numeric = Number(rawValue);
    result[key] = Number.isFinite(numeric) ? numeric : rawValue;
  }

  return result;
}

function numericRecordFromLines(value: FormDataEntryValue | null) {
  const result: Record<string, number> = {};

  for (const line of String(value || "").split("\n")) {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey?.trim();
    const numeric = Number(rest.join(":").trim());

    if (key && Number.isFinite(numeric)) {
      result[key] = numeric;
    }
  }

  return result;
}

function recordToLines(value: Record<string, string | number>) {
  return Object.entries(value)
    .map(([key, entry]) => `${key}: ${entry}`)
    .join("\n");
}

function numericRecordToLines(value: Record<string, number>) {
  return Object.entries(value)
    .map(([key, entry]) => `${key}: ${entry}`)
    .join("\n");
}

function featuresFromText(value: FormDataEntryValue | null): CreatureFeature[] {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawName, ...rest] = line.split("::");
      return {
        name: rawName.trim() || "Feature",
        description: rest.join("::").trim(),
      };
    });
}

function featuresToText(features: CreatureFeature[]) {
  return features
    .map((feature) => `${feature.name} :: ${feature.description}`)
    .join("\n");
}

function safeSpellcasting(value: FormDataEntryValue | null): Record<string, unknown> {
  const raw = String(value || "").trim();
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    throw new Error("Spellcasting must be valid JSON.");
  }
}

function sourceLabel(creature: CreatureDefinition) {
  return creature.sourceType === "srd_api"
    ? `SRD ${creature.sourceRuleset || ""}`.trim()
    : "Homebrew";
}

function modifier(score?: number) {
  if (typeof score !== "number") return "—";
  const value = Math.floor((score - 10) / 2);
  return value >= 0 ? `+${value}` : String(value);
}

export function CreatureMaster({
  creatures,
  onAdd,
  onUpdate,
  onDelete,
  newId,
  now,
}: CreatureMasterProps) {
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [mode, setMode] = useState<
    null | "create" | "edit" | "view" | "import"
  >(null);
  const [formError, setFormError] = useState("");

  const selected = creatures.find((creature) => creature.id === selectedId);

  const types = useMemo(
    () =>
      Array.from(
        new Set(
          creatures
            .map((creature) => creature.creatureType)
            .filter(Boolean),
        ),
      ).sort(),
    [creatures],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return creatures
      .filter(
        (creature) =>
          typeFilter === "all" || creature.creatureType === typeFilter,
      )
      .filter(
        (creature) =>
          !q ||
          creature.name.toLowerCase().includes(q) ||
          creature.creatureType.toLowerCase().includes(q) ||
          creature.size.toLowerCase().includes(q) ||
          creature.lore.toLowerCase().includes(q),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [creatures, query, typeFilter]);

  const importedCount = creatures.filter(
    (creature) => creature.sourceType === "srd_api",
  ).length;
  const homebrewCount = creatures.length - importedCount;
  const legendaryCount = creatures.filter(
    (creature) => creature.legendaryActions.length > 0,
  ).length;

  function openCreate() {
    setSelectedId("");
    setFormError("");
    setMode("create");
  }

  function openEdit(id: string) {
    setSelectedId(id);
    setFormError("");
    setMode("edit");
  }

  function openView(id: string) {
    setSelectedId(id);
    setMode("view");
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    const form = event.currentTarget;
    const fd = new FormData(form);
    const timestamp = now();

    try {
      const abilities: Record<string, number> = {};
      for (const key of abilityKeys) {
        const value = numberOrUndefined(fd.get(key));
        if (value !== undefined) abilities[key] = value;
      }

      const base: CreatureDefinition =
        selected && mode === "edit"
          ? structuredClone(selected)
          : {
              id: newId(),
              workspaceId: "local",
              name: "",
              size: "",
              creatureType: "",
              alignment: "",
              hitDice: "",
              speed: {},
              abilities: {},
              saves: {},
              skills: {},
              damageVulnerabilities: [],
              damageResistances: [],
              damageImmunities: [],
              conditionImmunities: [],
              senses: {},
              languages: [],
              traits: [],
              actions: [],
              bonusActions: [],
              reactions: [],
              legendaryActions: [],
              mythicActions: [],
              spellcasting: {},
              lore: "",
              gmNotes: "",
              sourceType: "homebrew",
              createdAt: timestamp,
              updatedAt: timestamp,
            };

      const creature: CreatureDefinition = {
        ...base,
        name: String(fd.get("name") || "").trim(),
        size: String(fd.get("size") || "").trim(),
        creatureType: String(fd.get("creatureType") || "").trim(),
        alignment: String(fd.get("alignment") || "").trim(),
        armorClass: numberOrUndefined(fd.get("armorClass")),
        hitPoints: numberOrUndefined(fd.get("hitPoints")),
        hitDice: String(fd.get("hitDice") || "").trim(),
        speed: recordFromLines(fd.get("speed")),
        abilities,
        saves: numericRecordFromLines(fd.get("saves")),
        skills: numericRecordFromLines(fd.get("skills")),
        damageVulnerabilities: csv(fd.get("damageVulnerabilities")),
        damageResistances: csv(fd.get("damageResistances")),
        damageImmunities: csv(fd.get("damageImmunities")),
        conditionImmunities: csv(fd.get("conditionImmunities")),
        senses: recordFromLines(fd.get("senses")),
        languages: csv(fd.get("languages")),
        challengeRating: numberOrUndefined(fd.get("challengeRating")),
        xp: numberOrUndefined(fd.get("xp")),
        proficiencyBonus: numberOrUndefined(fd.get("proficiencyBonus")),
        traits: featuresFromText(fd.get("traits")),
        actions: featuresFromText(fd.get("actions")),
        bonusActions: featuresFromText(fd.get("bonusActions")),
        reactions: featuresFromText(fd.get("reactions")),
        legendaryActions: featuresFromText(fd.get("legendaryActions")),
        mythicActions: featuresFromText(fd.get("mythicActions")),
        spellcasting: safeSpellcasting(fd.get("spellcasting")),
        lore: String(fd.get("lore") || ""),
        gmNotes: String(fd.get("gmNotes") || ""),
        updatedAt: timestamp,
      };

      if (!creature.name) {
        setFormError("Creature name is required.");
        return;
      }

      if (selected && mode === "edit") {
        onUpdate(creature);
      } else {
        onAdd(creature);
      }

      setMode(null);
      setSelectedId("");
    } catch (reason) {
      setFormError(
        reason instanceof Error ? reason.message : "Unable to save creature.",
      );
    }
  }

  function confirmDelete(creature: CreatureDefinition) {
    if (
      window.confirm(
        `Permanently delete creature definition "${creature.name}"? This cannot be undone.`,
      )
    ) {
      onDelete(creature.id);
    }
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Creature Master</h2>
          <p>
            A reusable creature library for canonical stat blocks. Import open
            SRD creatures or create homebrew definitions that Storywarden can
            later instantiate in encounters.
          </p>
        </div>
        <div className="top-actions">
          <button className="secondary" onClick={() => setMode("import")}>
            <Download size={16} />
            Import from SRD
          </button>
          <button className="primary" onClick={openCreate}>
            <Plus size={16} />
            Create Homebrew
          </button>
        </div>
      </div>

      <div className="item-master-stats">
        <div>
          <b>{creatures.length}</b>
          <span>Definitions</span>
        </div>
        <div>
          <b>{importedCount}</b>
          <span>SRD Imports</span>
        </div>
        <div>
          <b>{homebrewCount}</b>
          <span>Homebrew</span>
        </div>
        <div>
          <b>{legendaryCount}</b>
          <span>Legendary</span>
        </div>
      </div>

      <div className="creature-toolbar">
        <label className="creature-search">
          <Search size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search creatures..."
          />
        </label>
        <select
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <option value="all">All creature types</option>
          {types.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {!filtered.length ? (
        <div className="empty">
          <h3>No creature definitions found</h3>
          <p>Create a homebrew creature or import one from the open SRD.</p>
        </div>
      ) : (
        <div className="creature-table">
          <div className="creature-table-head">
            <span>Name</span>
            <span>Type</span>
            <span>CR</span>
            <span>AC / HP</span>
            <span>Source</span>
            <span></span>
          </div>
          {filtered.map((creature) => (
            <div className="creature-table-row" key={creature.id}>
              <div>
                <strong>{creature.name}</strong>
                <small>
                  {[creature.size, creature.alignment]
                    .filter(Boolean)
                    .join(" · ") || "Creature definition"}
                </small>
              </div>
              <span>{creature.creatureType || "—"}</span>
              <span>{creature.challengeRating ?? "—"}</span>
              <span>
                {creature.armorClass ?? "—"} / {creature.hitPoints ?? "—"}
              </span>
              <span>
                <span
                  className={
                    creature.sourceType === "srd_api"
                      ? "source-badge"
                      : "homebrew-badge"
                  }
                >
                  {sourceLabel(creature)}
                </span>
              </span>
              <div className="row-actions">
                <button
                  className="icon-btn"
                  title="View stat block"
                  onClick={() => openView(creature.id)}
                >
                  <Eye size={14} />
                </button>
                <button
                  className="icon-btn"
                  title="Edit creature"
                  onClick={() => openEdit(creature.id)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="icon-btn danger-icon"
                  title="Delete creature"
                  onClick={() => confirmDelete(creature)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === "import" && (
        <SrdCreatureImporter
          onClose={() => setMode(null)}
          onImport={(creature) => {
            onAdd(creature);
            setMode(null);
          }}
          newId={newId}
          now={now}
        />
      )}

      {(mode === "create" || mode === "edit") && (
        <CreatureForm
          creature={mode === "edit" ? selected : undefined}
          error={formError}
          onClose={() => {
            setMode(null);
            setSelectedId("");
          }}
          onSubmit={submit}
        />
      )}

      {mode === "view" && selected && (
        <CreatureStatBlock
          creature={selected}
          onClose={() => {
            setMode(null);
            setSelectedId("");
          }}
          onEdit={() => setMode("edit")}
        />
      )}
    </section>
  );
}

function CreatureForm({
  creature,
  error,
  onClose,
  onSubmit,
}: {
  creature?: CreatureDefinition;
  error: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <form
        className="modal creature-form-modal"
        onSubmit={onSubmit}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="eyebrow">CREATURE MASTER</div>
            <h2>{creature ? "Edit Creature" : "Create Homebrew Creature"}</h2>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {creature?.sourceType === "srd_api" && (
          <div className="notice">
            This creature was imported from the SRD. Editing changes your
            Worldwarden copy only.
          </div>
        )}

        {error && <div className="error-box">{error}</div>}

        <div className="form-grid">
          <label className="field">
            <span>Name *</span>
            <input name="name" required defaultValue={creature?.name || ""} />
          </label>
          <label className="field">
            <span>Creature type</span>
            <input
              name="creatureType"
              placeholder="dragon, humanoid, undead..."
              defaultValue={creature?.creatureType || ""}
            />
          </label>
          <label className="field">
            <span>Size</span>
            <select name="size" defaultValue={creature?.size || ""}>
              <option value="">—</option>
              {["Tiny", "Small", "Medium", "Large", "Huge", "Gargantuan"].map(
                (size) => (
                  <option key={size}>{size}</option>
                ),
              )}
            </select>
          </label>
          <label className="field">
            <span>Alignment</span>
            <input
              name="alignment"
              defaultValue={creature?.alignment || ""}
              placeholder="unaligned"
            />
          </label>
          <label className="field">
            <span>Armor Class</span>
            <input
              name="armorClass"
              type="number"
              defaultValue={creature?.armorClass ?? ""}
            />
          </label>
          <label className="field">
            <span>Hit Points</span>
            <input
              name="hitPoints"
              type="number"
              defaultValue={creature?.hitPoints ?? ""}
            />
          </label>
          <label className="field">
            <span>Hit Dice</span>
            <input
              name="hitDice"
              defaultValue={creature?.hitDice || ""}
              placeholder="8d10+24"
            />
          </label>
          <label className="field">
            <span>Challenge Rating</span>
            <input
              name="challengeRating"
              type="number"
              step="0.125"
              defaultValue={creature?.challengeRating ?? ""}
            />
          </label>
          <label className="field">
            <span>XP</span>
            <input
              name="xp"
              type="number"
              defaultValue={creature?.xp ?? ""}
            />
          </label>
          <label className="field">
            <span>Proficiency Bonus</span>
            <input
              name="proficiencyBonus"
              type="number"
              defaultValue={creature?.proficiencyBonus ?? ""}
            />
          </label>
        </div>

        <h3 className="form-section-title">Ability Scores</h3>
        <div className="ability-form-grid">
          {abilityKeys.map((key) => (
            <label className="field" key={key}>
              <span>{key.toUpperCase()}</span>
              <input
                name={key}
                type="number"
                defaultValue={creature?.abilities[key] ?? ""}
              />
            </label>
          ))}
        </div>

        <div className="form-grid">
          <label className="field">
            <span>Speed, one per line (key: value)</span>
            <textarea
              name="speed"
              defaultValue={recordToLines(creature?.speed || {})}
              placeholder={"walk: 30 ft.\nfly: 60 ft."}
            />
          </label>
          <label className="field">
            <span>Senses, one per line (key: value)</span>
            <textarea
              name="senses"
              defaultValue={recordToLines(creature?.senses || {})}
              placeholder={"darkvision: 60 ft.\npassive_perception: 14"}
            />
          </label>
          <label className="field">
            <span>Saving Throws (key: bonus)</span>
            <textarea
              name="saves"
              defaultValue={numericRecordToLines(creature?.saves || {})}
              placeholder={"dex: 5\nwis: 4"}
            />
          </label>
          <label className="field">
            <span>Skills (key: bonus)</span>
            <textarea
              name="skills"
              defaultValue={numericRecordToLines(creature?.skills || {})}
              placeholder={"Perception: 6\nStealth: 5"}
            />
          </label>
        </div>

        <div className="form-grid">
          <label className="field">
            <span>Languages, comma separated</span>
            <input
              name="languages"
              defaultValue={creature?.languages.join(", ") || ""}
            />
          </label>
          <label className="field">
            <span>Damage vulnerabilities</span>
            <input
              name="damageVulnerabilities"
              defaultValue={creature?.damageVulnerabilities.join(", ") || ""}
            />
          </label>
          <label className="field">
            <span>Damage resistances</span>
            <input
              name="damageResistances"
              defaultValue={creature?.damageResistances.join(", ") || ""}
            />
          </label>
          <label className="field">
            <span>Damage immunities</span>
            <input
              name="damageImmunities"
              defaultValue={creature?.damageImmunities.join(", ") || ""}
            />
          </label>
          <label className="field">
            <span>Condition immunities</span>
            <input
              name="conditionImmunities"
              defaultValue={creature?.conditionImmunities.join(", ") || ""}
            />
          </label>
        </div>

        <h3 className="form-section-title">Features and Actions</h3>
        <p className="form-help">
          One entry per line using <strong>Name :: Description</strong>.
        </p>
        <div className="form-grid">
          <label className="field">
            <span>Traits</span>
            <textarea
              name="traits"
              defaultValue={featuresToText(creature?.traits || [])}
            />
          </label>
          <label className="field">
            <span>Actions</span>
            <textarea
              name="actions"
              defaultValue={featuresToText(creature?.actions || [])}
            />
          </label>
          <label className="field">
            <span>Bonus Actions</span>
            <textarea
              name="bonusActions"
              defaultValue={featuresToText(creature?.bonusActions || [])}
            />
          </label>
          <label className="field">
            <span>Reactions</span>
            <textarea
              name="reactions"
              defaultValue={featuresToText(creature?.reactions || [])}
            />
          </label>
          <label className="field">
            <span>Legendary Actions</span>
            <textarea
              name="legendaryActions"
              defaultValue={featuresToText(creature?.legendaryActions || [])}
            />
          </label>
          <label className="field">
            <span>Mythic Actions</span>
            <textarea
              name="mythicActions"
              defaultValue={featuresToText(creature?.mythicActions || [])}
            />
          </label>
        </div>

        <label className="field">
          <span>Spellcasting JSON</span>
          <textarea
            className="code-field"
            name="spellcasting"
            defaultValue={
              Object.keys(creature?.spellcasting || {}).length
                ? JSON.stringify(creature?.spellcasting, null, 2)
                : ""
            }
            placeholder='{"description":"Innate spellcasting..."}'
          />
        </label>

        <div className="form-grid">
          <label className="field">
            <span>Lore</span>
            <textarea name="lore" defaultValue={creature?.lore || ""} />
          </label>
          <label className="field">
            <span>GM Notes</span>
            <textarea name="gmNotes" defaultValue={creature?.gmNotes || ""} />
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary">
            {creature ? "Save Creature" : "Create Creature"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreatureStatBlock({
  creature,
  onClose,
  onEdit,
}: {
  creature: CreatureDefinition;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal creature-stat-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="eyebrow">CREATURE MASTER / STAT BLOCK</div>
            <h2>{creature.name}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="creature-subtitle">
          {[creature.size, creature.creatureType, creature.alignment]
            .filter(Boolean)
            .join(" · ")}
        </div>

        <div className="creature-core-stats">
          <span>
            <b>Armor Class</b>
            {creature.armorClass ?? "—"}
          </span>
          <span>
            <b>Hit Points</b>
            {creature.hitPoints ?? "—"}{" "}
            {creature.hitDice ? `(${creature.hitDice})` : ""}
          </span>
          <span>
            <b>Challenge</b>
            {creature.challengeRating ?? "—"}
          </span>
          <span>
            <b>XP</b>
            {creature.xp ?? "—"}
          </span>
          <span>
            <b>Proficiency</b>
            {creature.proficiencyBonus !== undefined
              ? `+${creature.proficiencyBonus}`
              : "—"}
          </span>
        </div>

        <div className="ability-grid">
          {abilityKeys.map((key) => (
            <div key={key}>
              <b>{key.toUpperCase()}</b>
              <strong>{creature.abilities[key] ?? "—"}</strong>
              <span>{modifier(creature.abilities[key])}</span>
            </div>
          ))}
        </div>

        <StatLine label="Speed" value={recordToLines(creature.speed).replaceAll("\n", ", ")} />
        <StatLine label="Saving Throws" value={numericRecordToLines(creature.saves).replaceAll("\n", ", ")} />
        <StatLine label="Skills" value={numericRecordToLines(creature.skills).replaceAll("\n", ", ")} />
        <StatLine label="Damage Vulnerabilities" value={creature.damageVulnerabilities.join(", ")} />
        <StatLine label="Damage Resistances" value={creature.damageResistances.join(", ")} />
        <StatLine label="Damage Immunities" value={creature.damageImmunities.join(", ")} />
        <StatLine label="Condition Immunities" value={creature.conditionImmunities.join(", ")} />
        <StatLine label="Senses" value={recordToLines(creature.senses).replaceAll("\n", ", ")} />
        <StatLine label="Languages" value={creature.languages.join(", ")} />

        <FeatureSection title="Traits" features={creature.traits} />
        <FeatureSection title="Actions" features={creature.actions} />
        <FeatureSection title="Bonus Actions" features={creature.bonusActions} />
        <FeatureSection title="Reactions" features={creature.reactions} />
        <FeatureSection
          title="Legendary Actions"
          features={creature.legendaryActions}
        />
        <FeatureSection title="Mythic Actions" features={creature.mythicActions} />

        {creature.lore && (
          <section className="stat-section">
            <h3>Lore</h3>
            <p>{creature.lore}</p>
          </section>
        )}

        {creature.gmNotes && (
          <section className="stat-section gm-note-section">
            <h3>GM Notes</h3>
            <p>{creature.gmNotes}</p>
          </section>
        )}

        <div className="source-line">
          {sourceLabel(creature)}
          {creature.sourceUrl ? " · Source record retained" : ""}
        </div>

        <div className="form-actions">
          <button className="secondary" onClick={onClose}>
            Close
          </button>
          <button className="primary" onClick={onEdit}>
            <Pencil size={15} />
            Edit Creature
          </button>
        </div>
      </div>
    </div>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  if (!value) return null;

  return (
    <div className="stat-line">
      <b>{label}</b>
      <span>{value}</span>
    </div>
  );
}

function FeatureSection({
  title,
  features,
}: {
  title: string;
  features: CreatureFeature[];
}) {
  if (!features.length) return null;

  return (
    <section className="stat-section">
      <h3>{title}</h3>
      {features.map((feature, index) => (
        <p key={`${feature.name}-${index}`}>
          <strong>{feature.name}.</strong> {feature.description}
          {feature.attackBonus !== undefined
            ? ` Attack +${feature.attackBonus}.`
            : ""}
          {feature.damage ? ` Damage ${feature.damage}.` : ""}
          {feature.dc !== undefined ? ` DC ${feature.dc}.` : ""}
        </p>
      ))}
    </section>
  );
}
